/**
 * NotificationBell - Displays notification bell icon with unread count badge
 * and dropdown list of recent notifications
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Check, ExternalLink, X } from 'lucide-react';
import { Button } from '@/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/ui/dropdown-menu';
import { ScrollArea } from '@/ui/scroll-area';
import useStore from '../store/useStore';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Notification type icons/colors
const NOTIFICATION_STYLES = {
  campaign: { bg: 'bg-blue-500/20', border: 'border-blue-500/30', text: 'text-blue-400' },
  design_assigned: { bg: 'bg-teal-500/20', border: 'border-teal-500/30', text: 'text-teal-400' },
  design_updated: { bg: 'bg-purple-500/20', border: 'border-purple-500/30', text: 'text-purple-400' },
  booking_approved: { bg: 'bg-green-500/20', border: 'border-green-500/30', text: 'text-green-400' },
  booking_rejected: { bg: 'bg-red-500/20', border: 'border-red-500/30', text: 'text-red-400' },
  booking_rescheduled: { bg: 'bg-orange-500/20', border: 'border-orange-500/30', text: 'text-orange-400' },
  installation_scheduled: { bg: 'bg-indigo-500/20', border: 'border-indigo-500/30', text: 'text-indigo-400' },
  installation_updated: { bg: 'bg-yellow-500/20', border: 'border-yellow-500/30', text: 'text-yellow-400' },
  installation_completed: { bg: 'bg-emerald-500/20', border: 'border-emerald-500/30', text: 'text-emerald-400' },
  general: { bg: 'bg-slate-500/20', border: 'border-slate-500/30', text: 'text-slate-400' },
};

const NotificationItem = ({ notification, onMarkRead, onNavigate }) => {
  const style = NOTIFICATION_STYLES[notification.type] || NOTIFICATION_STYLES.general;
  const timeAgo = formatTimeAgo(notification.createdAt);
  
  return (
    <div 
      className={`p-3 border-b border-slate-700/50 hover:bg-slate-700/30 transition-colors ${
        notification.isRead ? 'opacity-60' : ''
      }`}
    >
      <div className="flex items-start gap-3">
        {/* Unread indicator */}
        {!notification.isRead && (
          <div className="w-2 h-2 bg-teal-400 rounded-full mt-2 flex-shrink-0" />
        )}
        {notification.isRead && <div className="w-2 flex-shrink-0" />}
        
        {/* Content */}
        <div className="flex-1 min-w-0">
          <h4 className={`text-sm font-medium ${notification.isRead ? 'text-slate-400' : 'text-white'}`}>
            {notification.title}
          </h4>
          <p className="text-xs text-slate-400 mt-0.5 line-clamp-2">
            {notification.message}
          </p>
          <span className="text-xs text-slate-500 mt-1 block">{timeAgo}</span>
        </div>
        
        {/* Actions */}
        <div className="flex items-center gap-1 flex-shrink-0">
          {notification.linkUrl && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 text-slate-400 hover:text-white"
              onClick={() => onNavigate(notification.linkUrl)}
            >
              <ExternalLink className="h-3 w-3" />
            </Button>
          )}
          {!notification.isRead && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 text-slate-400 hover:text-teal-400"
              onClick={() => onMarkRead(notification.id)}
            >
              <Check className="h-3 w-3" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

function formatTimeAgo(dateString) {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

const NotificationBell = ({ className = '' }) => {
  const currentUser = useStore((s) => s.currentUser);
  const authToken = useStore((s) => s.authToken);
  const setCurrentPage = useStore((s) => s.setCurrentPage);
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Use authToken from store or localStorage
  const token = authToken || localStorage.getItem('eoc-auth-token');
  
  // Fetch unread count on mount and periodically
  const fetchUnreadCount = useCallback(async () => {
    if (!token) return;
    
    try {
      const res = await fetch(`${API_URL}/api/notifications/unread-count`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setUnreadCount(data.unreadCount);
      }
    } catch (err) {
      console.error('Failed to fetch unread count:', err);
    }
  }, [token]);
  
  // Fetch full notifications when dropdown opens
  const fetchNotifications = useCallback(async () => {
    if (!token) return;
    
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/notifications?page=1&pageSize=10`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setNotifications(data.notifications);
        setUnreadCount(data.unreadCount);
      }
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);
  
  // Initial fetch and periodic refresh
  useEffect(() => {
    if (token) {
      fetchUnreadCount();
      // Refresh every 60 seconds
      const interval = setInterval(fetchUnreadCount, 60000);
      return () => clearInterval(interval);
    }
  }, [token, fetchUnreadCount]);
  
  // Fetch when dropdown opens
  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
    }
  }, [isOpen, fetchNotifications]);
  
  // Mark single notification as read
  const handleMarkRead = async (notificationId) => {
    if (!token) return;
    
    try {
      await fetch(`${API_URL}/api/notifications/${notificationId}/read`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      // Update local state
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, isRead: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Failed to mark notification as read:', err);
    }
  };
  
  // Mark all as read
  const handleMarkAllRead = async () => {
    if (!token) return;
    
    try {
      await fetch(`${API_URL}/api/notifications/mark-all-read`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error('Failed to mark all as read:', err);
    }
  };
  
  // Navigate to linked page
  const handleNavigate = (linkUrl) => {
    setIsOpen(false);
    // Map URL to page name
    const urlToPage = {
      '/saved-designs': 'saved-designs',
      '/booking': 'booking',
      '/review': 'review',
      '/admin': 'admin',
    };
    const pageName = urlToPage[linkUrl] || 'landing';
    setCurrentPage(pageName);
  };
  
  // Don't render if not logged in
  if (!currentUser) return null;
  
  return (
    <DropdownMenu modal={false} open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={`relative text-slate-400 hover:text-white h-8 w-8 p-0 ${className}`}
          data-testid="notification-bell-btn"
        >
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span 
              className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center"
              data-testid="notification-badge"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      
      <DropdownMenuContent 
        align="end" 
        className="w-80 bg-slate-800 border-slate-700 p-0"
        data-testid="notification-dropdown"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-3 border-b border-slate-700">
          <h3 className="text-sm font-semibold text-white">Notifications</h3>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="text-xs text-teal-400 hover:text-teal-300 h-auto p-1"
              onClick={handleMarkAllRead}
            >
              Mark all read
            </Button>
          )}
        </div>
        
        {/* Notifications List */}
        <ScrollArea className="h-[300px]">
          {loading ? (
            <div className="flex items-center justify-center p-8">
              <div className="w-6 h-6 border-2 border-teal-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center p-8 text-slate-400">
              <Bell className="h-8 w-8 mb-2 opacity-50" />
              <p className="text-sm">No notifications yet</p>
            </div>
          ) : (
            notifications.map(notification => (
              <NotificationItem
                key={notification.id}
                notification={notification}
                onMarkRead={handleMarkRead}
                onNavigate={handleNavigate}
              />
            ))
          )}
        </ScrollArea>
        
        {/* Footer */}
        {notifications.length > 0 && (
          <div className="p-2 border-t border-slate-700 text-center">
            <Button
              variant="ghost"
              size="sm"
              className="text-xs text-slate-400 hover:text-white"
              onClick={() => {
                setIsOpen(false);
                navigate('/notifications');
              }}
              data-testid="view-all-notifications-btn"
            >
              View all notifications
            </Button>
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default NotificationBell;
